import type { ReportGenerationPort } from "@/application/records/ports/ReportGenerationPort";
import type { RecordId } from "@/domain/records/types";
import type { GenerationRequest } from "@/domain/records/value-objects/GenerationRequest";
import { createIdempotencyKey } from "../../http/request-id";
import { httpClient, type TransportRequestConfig } from "../../http/HttpClient";

const REPORT_GENERATION_START_TRANSPORT_CONFIG: TransportRequestConfig = {
  timeoutMs: 45_000,
  maxAttempts: 1,
};

type GenerationStatusResponse = {
  status: string;
  progress?: number;
  message?: string;
  error?: string;
  [key: string]: unknown;
};

/**
 * Report Generation Adapter implementation
 * Handles communication with Generation API endpoints
 */
export class ReportGenerationAdapter implements ReportGenerationPort {
  constructor(
    private readonly baseUrl: string,
    private readonly authHeadersProvider?: () => Promise<Record<string, string>>
  ) {}

  async generate(request: GenerationRequest): Promise<void> {
    const url = `${this.baseUrl}/insight/${request.recordId}/generate/`;
    const shouldSendResearchContext = request.includesReports;

    const payload = this.buildPayload({
      report_types: request.reportTypes,
      custom_prompt: request.customPrompt,
      goals: shouldSendResearchContext ? request.goals : undefined, // goals are not needed for transcript-only flow
      guide: shouldSendResearchContext ? request.guide : undefined, // guide is not needed for transcript-only flow
      notes: request.notes, // notes (additional info)
      participant_count: request.participantCount,
      respondent_known_names:
        request.filteredRespondentNames.length > 0
          ? request.filteredRespondentNames
          : undefined,
      language: request.languageForApi,
      report_language: request.reportLanguageForApi,
      stt_provider: request.sttProviderForApi,
      is_with_transcript: request.includesTranscript,
      is_with_insight_report: request.includesReports,
    });

    await this.makeRequest<undefined>(url, {
      method: "POST",
      body: JSON.stringify(payload),
    }, {
      idempotencyKey: createIdempotencyKey("report-generate", request.recordId, request.reportTypes.join(",")),
      ...REPORT_GENERATION_START_TRANSPORT_CONFIG,
    });
  }

  async regenerate(request: GenerationRequest): Promise<void> {
    const url = `${this.baseUrl}/insight/${request.recordId}/regenerate/`;
    const shouldSendResearchContext = request.includesReports;

    const payload = this.buildPayload({
      report_types: request.reportTypes,
      custom_prompt: request.customPrompt,
      goals: shouldSendResearchContext ? request.goals : undefined, // goals are not needed for transcript-only flow
      guide: shouldSendResearchContext ? request.guide : undefined, // guide is not needed for transcript-only flow
      notes: request.notes, // notes (additional info)
      participant_count: request.participantCount,
      respondent_known_names:
        request.filteredRespondentNames.length > 0
          ? request.filteredRespondentNames
          : undefined,
      language: request.languageForApi,
      report_language: request.reportLanguageForApi,
      stt_provider: request.sttProviderForApi,
      is_with_transcript: request.includesTranscript,
      is_with_insight_report: request.includesReports,
    });

    await this.makeRequest<undefined>(url, {
      method: "POST",
      body: JSON.stringify(payload),
    }, {
      idempotencyKey: createIdempotencyKey("report-regenerate", request.recordId, request.reportTypes.join(",")),
      ...REPORT_GENERATION_START_TRANSPORT_CONFIG,
    });
  }

  async cancel(recordId: RecordId): Promise<void> {
    const url = `${this.baseUrl}/insight/${recordId}/cancel-generation/`;

    await this.makeRequest<undefined>(url, {
      method: "POST",
    }, {
      idempotencyKey: createIdempotencyKey("report-cancel", recordId),
    });
  }

  async getStatus(recordId: RecordId): Promise<GenerationStatusResponse> {
    // Guard: return default status if recordId is empty (e.g., in useBatchGenerationProgress with empty slots)
    // This prevents 404 errors when hook is called with empty string
    if (!recordId || recordId.trim() === "") {
      return {
        status: "idle",
        progress: 0,
      };
    }

    const url = `${this.baseUrl}/insight/${recordId}/pipeline-status/`;

    try {
      return await this.makeRequest<GenerationStatusResponse>(url);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Generic request method with error handling
   */
  private async makeRequest<TResponse = unknown>(
    url: string,
    options: RequestInit = {},
    transportConfig: TransportRequestConfig = {}
  ): Promise<TResponse> {
    try {
      // Use HttpClient which handles automatic token refresh
      const response = await httpClient.request(url, options, transportConfig);

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (
            errorData &&
            typeof errorData === "object" &&
            "detail" in errorData
          ) {
            errorMessage = String((errorData as { detail: unknown }).detail);
          }
        } catch {
          // Ignore JSON parsing errors
        }
        throw new Error(errorMessage);
      }

      if (response.status === 204) {
        return undefined as TResponse;
      }

      const raw = await response.text();
      if (!raw.trim()) {
        return undefined as TResponse;
      }

      try {
        return JSON.parse(raw) as TResponse;
      } catch {
        return raw as unknown as TResponse;
      }
    } catch (error) {
      console.error("ReportGenerationAdapter request failed:", error);
      throw error; // Re-throw to preserve error details
    }
  }

  private buildPayload(
    payload: Record<string, unknown>
  ): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(payload)) {
      if (value === undefined || value === null) {
        continue;
      }

      sanitized[key] = value;
    }

    return sanitized;
  }

  // buildHeaders method removed - HttpClient handles headers automatically
}
